import type { ExistingFileController } from "@/hooks/use-existing-file-controller";
import { client } from "@/hono";
import { PickProjectGalleryFilesModal } from "@/modals/projects/pick-project-gallery-files-modal";
import { Button } from "@msuite/ui/button";
import type { FileUploadController } from "@msuite/ui/file-upload";
import { useDisclosure } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { Tables } from "@msuite/utils/types";

/** Props Interface */
interface TicketFormSelectExistingFilesProps {
	controller: FileUploadController;
	projectId: string;
	ticketId?: string | null;
	existingFileController?: ExistingFileController;
}

export function TicketFormSelectExistingFiles({
	controller,
	projectId,
	ticketId,
	existingFileController,
}: TicketFormSelectExistingFilesProps) {
	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const qc = useQueryClient();

	/** Functions */
	async function handleOnComplete(fileIds: string[]) {
		if (fileIds.length < 1) return;
		controller.handleOnAddDefaultFiles(fileIds.map((id) => ({ id }) as Tables<"files">));
		if (ticketId) {
			const response = await client.tickets[":ticketId"].files.$post({
				param: { ticketId },
				json: { existingFileIds: fileIds },
			});
			await responseHandler(response);
			await qc.invalidateQueries({ predicate: invalidate([ticketId]) });
			return;
		}
		existingFileController?.handleOnAddPendingFileIds(fileIds);
	}

	/** Guard */
	// No rule is asked here: the form is the ticket's edit surface and stands under the ticket rule
	// already, which is also all the link route asks.
	if (!projectId || projectId === "NULL") return null;

	/** Render */
	return (
		<>
			<div className="h-stack justify-center mt-2">
				<Button
					variant="white"
					onClick={disclosure.onOpen}
					data-cy="ticket-select-existing-files-button"
				>
					{t("general.select_existing_files")}
				</Button>
			</div>
			{disclosure.isOpen && (
				<PickProjectGalleryFilesModal
					projectId={projectId}
					excludeFileIds={[
						...controller.defaultFiles.map((file) => file.id),
						...(existingFileController?.pendingFileIds ?? []),
					]}
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					onComplete={handleOnComplete}
				/>
			)}
		</>
	);
}
